import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { isBefore, startOfToday } from "date-fns";
import { fetchPatients, resolvePatient } from "./Patients";
import type { IListPatient } from "./Patients";
import { logisticsApi } from "./ApiClient";
import { medicalCompetenceSchema } from "@medoma/models/shifts";
import { generatePatches, generateQueryString } from "./Helpers";
import { deduplicatePrimitiveArray } from "@/Utils/arrayUtils";
import {
  calendarDateSchema,
  dateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import { addressWithCoordinates } from "@medoma/models/addresses";
import {
  categorySchema,
  isGroup,
  videoTypeSchema,
  measurementSchema,
} from "@medoma/models/activities";
import {
  unenrichedActivityOccurrencesAndGroupsSchema,
  unenrichedActivityOccurrencesAndGroupsWithRequiredPatientIdsSchema,
} from "@medoma/models/activities.unenriched";
import type {
  IActivityOccurrenceOrGroup,
  IActivityOccurrenceOrGroupWithRequiredPatient,
  IMeasurementsType,
} from "@medoma/models/activities";
import type {
  IUnenrichedActivityOccurrenceOrGroup,
  IUnenrichedGroupOfActivityOccurrence,
} from "@medoma/models/activities.unenriched";
import type { IMedicalCompetence } from "@medoma/models/shifts";
import { msg } from "@lingui/core/macro";

export const activityCategoryDictionary = {
  HomeVisit: msg`Hembesök`,
  VideoCall: msg`Videosamtal`,
  ClinicVisit: msg`Mottagningsbesök`,
  PatientTask: msg`Patientaktivitet`,
  AdminTask: msg`Administrativ uppgift`,
  PatientMeasurementTask: msg`Egen mätning`,
  PhoneCall: msg`Telefonsamtal`,
};

const fetchPatientsForActivityOccurrencesAndGroups = (
  activityOccurrencesAndGroups: IUnenrichedActivityOccurrenceOrGroup[],
) =>
  fetchPatients({
    patientIds: deduplicatePrimitiveArray(
      activityOccurrencesAndGroups.flatMap(({ patientId }) =>
        patientId ? [patientId] : [],
      ),
    ),
  });

type GroupWithPatient<T extends IUnenrichedGroupOfActivityOccurrence> =
  T extends IUnenrichedGroupOfActivityOccurrence
    ? StrictOmit<T, "patientId" | "occurrences"> & {
        patient: IListPatient;
        occurrences: Array<
          StrictOmit<T["occurrences"][number], "patientId"> & {
            patient: IListPatient;
          }
        >;
      }
    : never;

const enrichGroupWithPatient = <
  T extends IUnenrichedGroupOfActivityOccurrence,
>({
  unenrichedGroup,
  patient,
}: {
  unenrichedGroup: T;
  patient: IListPatient;
}): GroupWithPatient<T> => {
  const {
    patientId: _droppedPatientId,
    occurrences,
    ...group
  } = unenrichedGroup;

  // The parsed group guarantees that its category matches its occurrence type.
  // Mapping the union loses that correlation, so restore it at this boundary.
  return {
    ...group,
    patient,
    occurrences: occurrences.map(
      ({ patientId: _droppedPatientId, ...occurrence }) => ({
        ...occurrence,
        patient,
      }),
    ),
  } as GroupWithPatient<T>;
};

const enrichActivityOccurrenceOrGroupWithRequiredPatient = ({
  activityOccurrenceOrGroup,
  patient,
}: {
  activityOccurrenceOrGroup: IUnenrichedActivityOccurrenceOrGroup;
  patient: IListPatient;
}): IActivityOccurrenceOrGroupWithRequiredPatient => {
  if (isGroup(activityOccurrenceOrGroup)) {
    return enrichGroupWithPatient({
      unenrichedGroup: activityOccurrenceOrGroup,
      patient,
    });
  }

  const { patientId: _droppedPatientId, ...activityOccurrence } =
    activityOccurrenceOrGroup;
  return { ...activityOccurrence, patient };
};

const parseAndEnrichActivityOccurrencesAndGroups = async (
  probableActivityOccurrencesAndGroups: unknown,
): Promise<IActivityOccurrenceOrGroup[]> => {
  const parsedActivityOccurrencesAndGroups =
    unenrichedActivityOccurrencesAndGroupsSchema.parse(
      probableActivityOccurrencesAndGroups,
    );

  if (parsedActivityOccurrencesAndGroups.length === 0) {
    return [];
  }

  const patients = await fetchPatientsForActivityOccurrencesAndGroups(
    parsedActivityOccurrencesAndGroups,
  );

  // Enrichment replaces patientId with the resolved patient; the values are
  // already decoded, so this maps rather than re-parses.
  return parsedActivityOccurrencesAndGroups.map((activityOccurrenceOrGroup) => {
    // An admin task is the only category that can come without a patient.
    if (activityOccurrenceOrGroup.category === categorySchema.enum.AdminTask) {
      const { patientId, ...adminTask } = activityOccurrenceOrGroup;
      if (!patientId) {
        return { ...adminTask, patient: null };
      }

      return enrichActivityOccurrenceOrGroupWithRequiredPatient({
        activityOccurrenceOrGroup,
        patient: resolvePatient({ patientId, patients }),
      });
    }

    return enrichActivityOccurrenceOrGroupWithRequiredPatient({
      activityOccurrenceOrGroup,
      patient: resolvePatient({
        patientId: activityOccurrenceOrGroup.patientId,
        patients,
      }),
    });
  });
};

const parseAndEnrichActivityOccurrencesAndGroupsWithRequiredPatient = async (
  probableActivityOccurrencesAndGroups: unknown,
): Promise<IActivityOccurrenceOrGroupWithRequiredPatient[]> => {
  const parsedActivityOccurrencesAndGroups =
    unenrichedActivityOccurrencesAndGroupsWithRequiredPatientIdsSchema.parse(
      probableActivityOccurrencesAndGroups,
    );

  if (parsedActivityOccurrencesAndGroups.length === 0) {
    return [];
  }

  const patients = await fetchPatientsForActivityOccurrencesAndGroups(
    parsedActivityOccurrencesAndGroups,
  );

  return parsedActivityOccurrencesAndGroups.map((activityOccurrenceOrGroup) =>
    enrichActivityOccurrenceOrGroupWithRequiredPatient({
      activityOccurrenceOrGroup,
      patient: resolvePatient({
        patientId: activityOccurrenceOrGroup.patientId,
        patients,
      }),
    }),
  );
};

export const activityKeys = {
  all: ["activities"] as const,
  lists: () => [...activityKeys.all, "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...activityKeys.lists(), filters] as const,
  detail: (id: string) => [...activityKeys.all, id, "details"] as const,
};

const activityOccurrenceAndGroupKeys = {
  all: () => [...activityKeys.all, "occurrences"] as const,
  lists: () => [...activityOccurrenceAndGroupKeys.all(), "list"] as const,
  list: (filters: Record<string, unknown>) =>
    [...activityOccurrenceAndGroupKeys.lists(), filters] as const,
  detail: (id: string) =>
    [...activityOccurrenceAndGroupKeys.all(), id, "details"] as const,
};

export const finishActivityOccurrence = async (
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}/finish`,
  );
};

export const finishOccurrenceGroup = async ({
  id,
  occurrenceIds,
}: {
  id: string;
  occurrenceIds: string[];
}) => {
  await logisticsApi.post(`/ambulating/occurrence-groups/${id}/finish`, {
    json: { occurrenceIds },
  });
};

export const finishCareEpisodeActivityOccurrences = async ({
  careEpisodeId,
  occurrenceIds,
}: {
  careEpisodeId: string;
  occurrenceIds: string[];
}) => {
  await logisticsApi.post(
    `/ambulating/care-episodes/${careEpisodeId}/occurrences/finish`,
    {
      json: { occurrenceIds },
    },
  );
};

export const ANY_TIME_OF_DAY = "AnyTimeOfDay" as const;
const anyTimeOfDaySchema = z.literal(ANY_TIME_OF_DAY);

// Matches time in HH:MM format (24-hour clock)
// - Hours (HH): 00-23 (requires leading zero)
// - Minutes (MM): 00-59 (requires leading zero)
// Examples: "09:30", "14:00", "23:59"
const timeRegex = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

const timeSchema = z.union([z.string().regex(timeRegex), anyTimeOfDaySchema]);
export type ITime = z.infer<typeof timeSchema>;

const timesSchema = z.union([z.array(z.string()), anyTimeOfDaySchema]);

export const weekdaySchema = z.enum([
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]);
export type IWeekday = z.infer<typeof weekdaySchema>;

const intervalSchema = z.number().int().positive();

const frequencyBaseSchema = z.object({
  start: dateSchema,
  end: dateSchema.nullish(),
  span: z.string(),
  times: timesSchema,
});

const weekdayFrequencySchema = frequencyBaseSchema.extend({
  days: z.array(weekdaySchema),
});

const intervalFrequencySchema = frequencyBaseSchema.extend({
  interval: intervalSchema,
});

const frequencySchema = z.union([
  weekdayFrequencySchema,
  intervalFrequencySchema,
]);
export type IRecurringActivitySchedule = z.infer<typeof frequencySchema>;

const _rescheduleActivitySchema = z.union([
  weekdayFrequencySchema.omit({ start: true, end: true }).extend({
    span: z.string().optional(),
  }),
  intervalFrequencySchema.omit({ start: true, end: true }).extend({
    span: z.string().optional(),
  }),
]);
export type IRescheduleActivity = z.infer<typeof _rescheduleActivitySchema>;

const oneTimeSchema = z.object({
  date: z.string(),
  time: timeSchema,
  span: z.string(),
});

const activityScheduleSchema = z.union([oneTimeSchema, frequencySchema]);

const activityStatusSchema = z.enum(["active", "closed"]);

const activityBaseSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  description: z.string().nullish(),
  schedule: activityScheduleSchema,
  recurring: z.boolean(),
  category: categorySchema,
  duration: z.number().int(),
  status: activityStatusSchema,
  hidden: z.boolean(),
});

const newActivityOmit = {
  id: true,
  recurring: true,
  schedule: true,
  status: true,
} as const;

const newActivityExtend = {
  activityId: z.uuid(),
  timespan: z.string(),
  startDate: calendarDateSchema,
  templateId: z.uuid().optional(),
  templateRevision: z.number().int().optional(),
};

const oneTimeExtend = {
  time: timeSchema,
};

const customRecurrenceExtend = {
  days: z.array(weekdaySchema),
  times: timesSchema,
  endDate: calendarDateSchema.optional(),
};

const preconfiguredRecurrenceExtend = {
  interval: intervalSchema,
  times: timesSchema,
  endDate: calendarDateSchema.optional(),
};

// HOME VISIT
const activityHomeVisitSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.HomeVisit),
  doubleStaffing: z.boolean(),
  requiredCompetences: z.array(medicalCompetenceSchema),
  patientId: z.uuid(),
});

const newHomeVisitActivityBaseSchema = activityHomeVisitSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeHomeVisitActivitySchema =
  newHomeVisitActivityBaseSchema.extend(oneTimeExtend);

const newRecurringHomeVisitActivitySchema = z.union([
  newHomeVisitActivityBaseSchema.extend(customRecurrenceExtend),
  newHomeVisitActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newHomeVisitActivitySchema = z.union([
  newOneTimeHomeVisitActivitySchema,
  newRecurringHomeVisitActivitySchema,
]);

export type INewHomeVisitActivity = z.infer<typeof newHomeVisitActivitySchema>;

// VIDEO
const activityVideoSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.VideoCall),
  requiredCompetences: z.array(medicalCompetenceSchema),
  type: videoTypeSchema,
  patientId: z.uuid(),
});

const newVideoActivityBaseSchema = activityVideoSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeVideoActivitySchema =
  newVideoActivityBaseSchema.extend(oneTimeExtend);

const newRecurringVideoActivitySchema = z.union([
  newVideoActivityBaseSchema.extend(customRecurrenceExtend),
  newVideoActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newVideoActivitySchema = z.union([
  newOneTimeVideoActivitySchema,
  newRecurringVideoActivitySchema,
]);

export type INewVideoActivity = z.infer<typeof newVideoActivitySchema>;

// CLINIC VISIT
const activityClinicVisitSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.ClinicVisit),
  requiredCompetences: z.array(medicalCompetenceSchema),
  patientId: z.uuid(),
  address: addressWithCoordinates.nullish(),
});

const newClinicVisitActivityBaseSchema = activityClinicVisitSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeClinicVisitActivitySchema =
  newClinicVisitActivityBaseSchema.extend(oneTimeExtend);

const newRecurringClinicVisitActivitySchema = z.union([
  newClinicVisitActivityBaseSchema.extend(customRecurrenceExtend),
  newClinicVisitActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newClinicVisitActivitySchema = z.union([
  newOneTimeClinicVisitActivitySchema,
  newRecurringClinicVisitActivitySchema,
]);

export type INewClinicVisitActivity = z.infer<
  typeof newClinicVisitActivitySchema
>;

// PATIENT TASK
const activityPatientTaskSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.PatientTask),
  patientId: z.uuid(),
});

const newPatientTaskActivityBaseSchema = activityPatientTaskSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimePatientTaskActivitySchema =
  newPatientTaskActivityBaseSchema.extend(oneTimeExtend);

const newRecurringPatientTaskActivitySchema = z.union([
  newPatientTaskActivityBaseSchema.extend(customRecurrenceExtend),
  newPatientTaskActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newPatientTaskActivitySchema = z.union([
  newOneTimePatientTaskActivitySchema,
  newRecurringPatientTaskActivitySchema,
]);

export type INewPatientTaskActivity = z.infer<
  typeof newPatientTaskActivitySchema
>;

// ADMIN TASK
const activityAdminTaskSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.AdminTask),
  patientId: z.uuid().nullish(),
  requiredCompetences: z.array(medicalCompetenceSchema),
});

const newAdminTaskActivityBaseSchema = activityAdminTaskSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimeAdminTaskActivitySchema =
  newAdminTaskActivityBaseSchema.extend(oneTimeExtend);

const newRecurringAdminTaskActivitySchema = z.union([
  newAdminTaskActivityBaseSchema.extend(customRecurrenceExtend),
  newAdminTaskActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newAdminTaskActivitySchema = z.union([
  newOneTimeAdminTaskActivitySchema,
  newRecurringAdminTaskActivitySchema,
]);

export type INewAdminTaskActivity = z.infer<typeof newAdminTaskActivitySchema>;

// PHONE CALL
const activityPhoneCallSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.PhoneCall),
  requiredCompetences: z.array(medicalCompetenceSchema),
  patientId: z.uuid(),
});

const newPhoneCallActivityBaseSchema = activityPhoneCallSchema
  .omit(newActivityOmit)
  .extend(newActivityExtend);

const newOneTimePhoneCallActivitySchema =
  newPhoneCallActivityBaseSchema.extend(oneTimeExtend);

const newRecurringPhoneCallActivitySchema = z.union([
  newPhoneCallActivityBaseSchema.extend(customRecurrenceExtend),
  newPhoneCallActivityBaseSchema.extend(preconfiguredRecurrenceExtend),
]);

const newPhoneCallActivitySchema = z.union([
  newOneTimePhoneCallActivitySchema,
  newRecurringPhoneCallActivitySchema,
]);

export type INewPhoneCallActivity = z.infer<typeof newPhoneCallActivitySchema>;

// PATIENT MEASUREMENT TASK
const activityPatientMeasurementTaskSchema = activityBaseSchema.extend({
  category: z.literal(categorySchema.enum.PatientMeasurementTask),
  patientId: z.uuid(),
  measurements: z.array(measurementSchema),
});

const newPatientMeasurementTaskActivityBaseSchema =
  activityPatientMeasurementTaskSchema
    .omit(newActivityOmit)
    .extend(newActivityExtend);

const newOneTimePatientMeasurementTaskActivitySchema =
  newPatientMeasurementTaskActivityBaseSchema.extend(oneTimeExtend);

const newRecurringPatientMeasurementTaskActivitySchema = z.union([
  newPatientMeasurementTaskActivityBaseSchema.extend(customRecurrenceExtend),
  newPatientMeasurementTaskActivityBaseSchema.extend(
    preconfiguredRecurrenceExtend,
  ),
]);

const newPatientMeasurementTaskActivitySchema = z.union([
  newOneTimePatientMeasurementTaskActivitySchema,
  newRecurringPatientMeasurementTaskActivitySchema,
]);

export type INewPatientMeasurementTaskActivity = z.infer<
  typeof newPatientMeasurementTaskActivitySchema
>;

const _newActivitySchema = z.union([
  newHomeVisitActivitySchema,
  newVideoActivitySchema,
  newClinicVisitActivitySchema,
  newPatientTaskActivitySchema,
  newAdminTaskActivitySchema,
  newPatientMeasurementTaskActivitySchema,
  newPhoneCallActivitySchema,
]);

export type INewActivity = z.infer<typeof _newActivitySchema>;

const encodeNewActivityDates = (newActivity: INewActivity) => ({
  ...newActivity,
  startDate: calendarDateSchema.encode(newActivity.startDate),
  endDate:
    "endDate" in newActivity
      ? calendarDateSchema.optional().encode(newActivity.endDate)
      : undefined,
});

export const addActivity = async (newActivity: INewActivity) => {
  await logisticsApi.post("/ambulating/activities", {
    json: encodeNewActivityDates(newActivity),
  });
};

export const addActivitiesFromGroupTemplate = async ({
  templateId,
  templateRevision,
  newActivities,
}: {
  templateId: string;
  templateRevision: number;
  newActivities: INewActivity[];
}) => {
  await logisticsApi.post("/ambulating/activities/bulk", {
    json: {
      templateId,
      templateRevision,
      activities: newActivities.map(encodeNewActivityDates),
    },
  });
};

const _newJitActivityInVisitSchema = newHomeVisitActivityBaseSchema
  .pick({
    activityId: true,
    title: true,
    description: true,
  })
  .extend({
    visitId: z.string(),
    routeId: z.uuid(),
  });

export type INewJitActivityInVisit = z.infer<
  typeof _newJitActivityInVisitSchema
>;

export const addJitActivityInVisit = async (
  newActivityInVisit: INewJitActivityInVisit,
) => {
  await logisticsApi.post("/ambulating/activities/contextual", {
    json: newActivityInVisit,
  });
};

const _newActivityInGroupSchema = newHomeVisitActivityBaseSchema
  .pick({
    activityId: true,
    title: true,
    description: true,
    requiredCompetences: true,
    duration: true,
  })
  .extend({
    groupId: z.string(),
  });

export type INewActivityInGroup = z.infer<typeof _newActivityInGroupSchema>;

export const addActivityInGroup = async (
  newActivityInGroup: INewActivityInGroup,
) => {
  await logisticsApi.post("/ambulating/activities/contextual", {
    json: newActivityInGroup,
  });
};

async function fetchPatientsActivityOccurrencesAndGroups(
  patientId: string,
  from?: string,
  to?: string,
) {
  return await parseAndEnrichActivityOccurrencesAndGroupsWithRequiredPatient(
    await logisticsApi
      .get(
        `/ambulating/patients/${patientId}/occurrences${generateQueryString({
          from,
          to,
          status: [
            activityStatusSchema.enum.active,
            activityStatusSchema.enum.closed,
          ],
        })}`,
      )
      .json(),
  );
}

export const usePatientsActivityOccurrencesAndGroups = (
  patientId: string,
  fromDate?: Date,
  toDate?: Date,
) => {
  const from = fromDate ? machineFormat(fromDate, "yyyy-MM-dd") : undefined;
  const to = toDate ? machineFormat(toDate, "yyyy-MM-dd") : undefined;
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.list({ patientId, from, to }),
    queryFn: () =>
      fetchPatientsActivityOccurrencesAndGroups(patientId, from, to),
  });
};

async function fetchActivityOccurrence(
  activityId: string,
  occurrenceId: string,
) {
  const [activityOccurrence] = await parseAndEnrichActivityOccurrencesAndGroups(
    [
      await logisticsApi
        .get(`/ambulating/activities/${activityId}/occurrences/${occurrenceId}`)
        .json(),
    ],
  );

  if (!activityOccurrence || isGroup(activityOccurrence)) {
    throw new Error(
      `Expected an occurrence for activity ${activityId}, occurrence ${occurrenceId}.`,
    );
  }

  return activityOccurrence;
}

export const useActivityOccurrence = (
  activityId: string,
  occurrenceId: string,
) => {
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.detail(
      `${activityId}${occurrenceId}`,
    ),
    queryFn: () => fetchActivityOccurrence(activityId, occurrenceId),
  });
};

export type IActivityScopeTab = "occurrence" | "series";

export const removeActivityOccurrence = async (
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.delete(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}`,
  );
};

export const closeActivity = async (activityId: string) => {
  await logisticsApi.delete(`/ambulating/activities/${activityId}`);
};

export const allocateShiftsToActivityOccurrence = async (
  shiftIds: number[],
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}/allocate`,
    {
      json: { shifts: shiftIds },
    },
  );
};

export const deallocateShiftsFromActivityOccurrence = async (
  shiftIds: number[],
  activityId: string,
  occurrenceId: string,
) => {
  await logisticsApi.post(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}/deallocate`,
    {
      json: { shifts: shiftIds },
    },
  );
};

async function fetchGroup(id: string) {
  const [group] = await parseAndEnrichActivityOccurrencesAndGroups([
    await logisticsApi.get(`/ambulating/occurrence-groups/${id}`).json(),
  ]);

  if (!group || !isGroup(group)) {
    throw new Error(`Expected a group of occurrences for group id ${id}.`);
  }

  return group;
}

export const useGroup = (id: string) => {
  return useQuery({
    queryKey: activityOccurrenceAndGroupKeys.detail(`${id}`),
    queryFn: () => fetchGroup(id),
  });
};

export const activitySchema = z.union([
  activityHomeVisitSchema,
  activityVideoSchema,
  activityClinicVisitSchema,
  activityPatientTaskSchema,
  activityAdminTaskSchema,
  activityPatientMeasurementTaskSchema,
  activityPhoneCallSchema,
]);

export type IActivity = z.infer<typeof activitySchema>;

async function fetchActivity(activityId: string) {
  const parsedActivity = await logisticsApi
    .get(`/ambulating/activities/${activityId}`)
    .json(activitySchema);
  return parsedActivity;
}

export const useActivity = (activityId: string) => {
  return useQuery({
    queryKey: activityKeys.detail(activityId),
    queryFn: () => fetchActivity(activityId),
  });
};

// One-time activities are listed with their time in UTC, unlike recurring ones
const withOneTimeActivityTimeInLocalTime = (activity: IActivity): IActivity => {
  if (
    activity.recurring ||
    !("time" in activity.schedule) ||
    activity.schedule.time === ANY_TIME_OF_DAY
  ) {
    return activity;
  }

  const { date, time } = activity.schedule;
  const [hours = 0, minutes = 0] = time.split(":").map(Number);
  // A plain date string is parsed as midnight UTC
  const utcDateTime = new Date(date);
  utcDateTime.setUTCHours(hours, minutes);

  return {
    ...activity,
    schedule: {
      ...activity.schedule,
      time: machineFormat(utcDateTime, "HH:mm"),
    },
  };
};

async function fetchActivitiesToCloseForPatient(patientId: string) {
  const activities = await logisticsApi
    .get(`/ambulating/patients/${patientId}/activities`)
    .json(z.array(activitySchema));

  const startToday = startOfToday();
  return activities
    .map(withOneTimeActivityTimeInLocalTime)
    .filter(
      (activity) =>
        activity.recurring ||
        !("date" in activity.schedule) ||
        !isBefore(new Date(activity.schedule.date), startToday),
    );
}

export const useActivitiesToCloseForPatient = (patientId: string) => {
  return useQuery({
    queryKey: activityKeys.list({ patientId }),
    queryFn: () => fetchActivitiesToCloseForPatient(patientId),
    // A failed fetch must not block declining or discharging: the form shows
    // the error itself and asks the user to confirm the activities are closed
    throwOnError: false,
  });
};

export const updateTitle = async (activityId: string, title: string) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ title }),
  });
};

export const updateDescription = async (
  activityId: string,
  description: string,
) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ description }),
  });
};

export const updateHidden = async (activityId: string, hidden: boolean) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ hidden }),
  });
};

export const updateRequiredCompetences = async (
  activityId: string,
  requiredCompetences: IMedicalCompetence[],
) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ competence: requiredCompetences }),
  });
};

export const updateMeasurements = async (
  activityId: string,
  measurements: IMeasurementsType[],
) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ measurements }),
  });
};

export const updateDoubleStaffing = async (
  activityId: string,
  doubleStaffing: boolean,
) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ doubleStaffing }),
  });
};

export const updateDuration = async (activityId: string, duration: number) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ duration }),
  });
};

export const updateEndDate = async (activityId: string, endDateTime: Date) => {
  await logisticsApi.patch(`/ambulating/activities/${activityId}`, {
    json: generatePatches({ endDate: endDateTime }),
  });
};

export const setActivityOccurrenceNote = async ({
  activityId,
  occurrenceId,
  note,
}: {
  activityId: string;
  occurrenceId: string;
  note: string;
}) => {
  await logisticsApi.post(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}/set-note`,
    { json: { note } },
  );
};

export type IRescheduledActivityOccurrence = {
  date: Date;
  time: ITime;
  span?: string;
};

export const rescheduleActivityOccurrence = async (
  activityId: string,
  occurrenceId: string,
  data: IRescheduledActivityOccurrence,
) => {
  await logisticsApi.post(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}/reschedule`,
    { json: { ...data, date: calendarDateSchema.encode(data.date) } },
  );
};

export const changeActivityOccurrenceTimespan = async (
  activityId: string,
  occurrenceId: string,
  timespan: string,
) => {
  await logisticsApi.post(
    `/ambulating/activities/${activityId}/occurrences/${occurrenceId}/change-timespan`,
    { json: { timespan } },
  );
};

export const rescheduleActivity = async (
  activityId: string,
  data: IRescheduleActivity,
) => {
  await logisticsApi.post(`/ambulating/activities/${activityId}/reschedule`, {
    json: data,
  });
};
